import {
  IBillingDetailConfiguration,
  IBillingDetailTiers,
} from '~/src/dash/api/billing-detail/billing-detail-models';
import {
  transformBillingDetailConfigurationFromAPI,
  transformBillingDetailConfigurationToAPI,
  transformBillingDetailTiersFromAPI,
  transformBillingDetailTiersToAPI,
} from '~/src/dash/api/billing-detail/billing-detail-utils';
import {
  addDomainAndTokenToUrl,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';

export async function getBillingDetailConfiguration(
  configurationID: string,
): Promise<[IBillingDetailConfiguration?, Error?]> {
  const reqUrl = addDomainAndTokenToUrl(`/billing_detail_configuration/${configurationID}`);

  try {
    const resp = (await restGet<any>(reqUrl, {})).parsedBody;
    return [transformBillingDetailConfigurationFromAPI(resp), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function getBillingDetailTiers(
  tiersID: string,
): Promise<[IBillingDetailTiers?, Error?]> {
  const reqUrl = addDomainAndTokenToUrl(`/billing_detail_tiers/${tiersID}`);

  try {
    const resp = (await restGet<any>(reqUrl, {})).parsedBody;
    return [transformBillingDetailTiersFromAPI(resp), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function getBillingDetailsConfiguration(): Promise<
  [IBillingDetailConfiguration[]?, Error?]
> {
  const reqUrl = addDomainAndTokenToUrl(`/billing_details_configuration`);
  try {
    const resp = await restGet<any>(reqUrl, {});
    const configurations = [] as IBillingDetailConfiguration[];
    for (const configuration of resp.parsedBody) {
      const parsedConfiguration = transformBillingDetailConfigurationFromAPI(configuration);
      configurations.push(parsedConfiguration);
    }
    return [configurations, undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function getBillingDetailsTiers(): Promise<[IBillingDetailTiers[]?, Error?]> {
  const reqUrl = addDomainAndTokenToUrl(`/billing_details_tiers`);
  try {
    const resp = await restGet<any>(reqUrl, {});
    const tiers = [] as IBillingDetailTiers[];
    for (const tier of resp.parsedBody) {
      const parsedtier = transformBillingDetailTiersFromAPI(tier);
      tiers.push(parsedtier);
    }
    return [tiers, undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function createBillingDetailConfiguration(
  configuration: IBillingDetailConfiguration,
): Promise<IBillingDetailConfiguration> {
  const reqUrl = addDomainAndTokenToUrl(`/billing_detail_configuration`);
  const parsedBillingDetail = transformBillingDetailConfigurationToAPI(configuration);
  return await restPost<IBillingDetailConfiguration>(reqUrl, parsedBillingDetail).then(
    (val: IHttpResponse<any>) => {
      return transformBillingDetailConfigurationFromAPI(val.parsedBody);
    },
  );
}

export async function createBillingDetailTiers(
  tiers: IBillingDetailTiers,
): Promise<IBillingDetailTiers> {
  const reqUrl = addDomainAndTokenToUrl(`/billing_detail_tiers`);
  const parsedBillingDetail = transformBillingDetailTiersToAPI(tiers);
  return await restPost<IBillingDetailTiers>(reqUrl, parsedBillingDetail).then(
    (val: IHttpResponse<any>) => {
      return transformBillingDetailTiersFromAPI(val.parsedBody);
    },
  );
}

// update Freewheel Site will execute a full or partial update of the resource.  A full update will include all fields,
// child associations and will overwrite this data.  A partial update will only update the fields given through
// the Freewheel Site param

// NOTE: Because a full object is required, make sure that all fields in the object are defined in the interface/transform
export async function updateBillingDetailConfiguration(
  configuration: IBillingDetailConfiguration,
): Promise<IBillingDetailConfiguration> {
  const reqUrl = addDomainAndTokenToUrl(`/billing_detail_configuration/${configuration.ID}`);
  const parsedBillingDetail = transformBillingDetailConfigurationToAPI(configuration);
  return await restPatch<IBillingDetailConfiguration>(reqUrl, parsedBillingDetail).then(
    (val: IHttpResponse<any>) => {
      return transformBillingDetailConfigurationFromAPI(val.parsedBody);
    },
  );
}

export async function updateBillingDetailTiers(
  tiers: IBillingDetailTiers,
): Promise<IBillingDetailTiers> {
  const reqUrl = addDomainAndTokenToUrl(`/billing_detail_tiers/${tiers.ID}`);
  const parsedBillingDetail = transformBillingDetailTiersToAPI(tiers);
  return await restPatch<IBillingDetailTiers>(reqUrl, parsedBillingDetail).then(
    (val: IHttpResponse<any>) => {
      return transformBillingDetailTiersFromAPI(val.parsedBody);
    },
  );
}

export async function deleteBillingDetailConfiguration(
  configurationID: string,
): Promise<IHttpResponse<IBillingDetailConfiguration>> {
  const reqUrl = addDomainAndTokenToUrl(`/billing_detail_configuration/${configurationID}`);
  return await restDelete<IBillingDetailConfiguration>(reqUrl, undefined);
}

export async function deleteBillingDetailTiers(
  tiersID: string,
): Promise<IHttpResponse<IBillingDetailTiers>> {
  const reqUrl = addDomainAndTokenToUrl(`/billing_detail_tiers/${tiersID}`);
  return await restDelete<IBillingDetailTiers>(reqUrl, undefined);
}
